import React from "react";
import { parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { CalendarEvent } from "../../utils/calendarUtils";
import {
  getUpcomingEvents,
  groupEventsByDate,
  formatDateHeader,
  EVENT_COLORS,
  EVENT_TYPE_LABELS,
  getEntityRoute,
} from "../../utils/calendarUtils";

interface UpcomingHitListProps {
  events: CalendarEvent[];
}

const UpcomingHitList: React.FC<UpcomingHitListProps> = ({ events }) => {
  const navigate = useNavigate();
  const upcoming = getUpcomingEvents(events, 2);
  const grouped = groupEventsByDate(upcoming);
  const today = new Date();

  // Generate sorted date keys
  const dateKeys = [...grouped.keys()].sort();

  if (upcoming.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">
          Upcoming (2 weeks)
        </h3>
        <p className="mt-3 text-center text-sm text-gray-400">
          No upcoming events
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Upcoming (2 weeks)
        </h3>
        <p className="text-xs text-gray-500">
          {upcoming.length} event{upcoming.length !== 1 ? "s" : ""}
        </p>
      </div>
      <div className="divide-y divide-gray-100">
        {dateKeys.map((dateKey) => {
          const dayEvents = grouped.get(dateKey) || [];
          const date = parseISO(dateKey);

          return (
            <div key={dateKey}>
              {/* Date header */}
              <div className="bg-gray-50 px-4 py-1.5">
                <span className="text-xs font-semibold uppercase text-gray-500">
                  {formatDateHeader(date, today)}
                </span>
              </div>

              {/* Events for this date */}
              {dayEvents.map((event) => {
                const colors = EVENT_COLORS[event.type];
                return (
                  <button
                    key={event.id}
                    onClick={() =>
                      navigate(getEntityRoute(event.type, event.entityId))
                    }
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
                  >
                    <span
                      className={`h-2 w-2 flex-shrink-0 rounded-full ${colors.dot}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-gray-900">
                        {event.title}
                      </div>
                      <div className="flex items-center gap-2">
                        {event.startTime && (
                          <span className="text-xs text-gray-500">
                            {event.startTime}
                          </span>
                        )}
                        {event.amount !== undefined && (
                          <span className="text-xs font-medium text-gray-600">
                            £
                            {(event.amount / 100).toLocaleString("en-GB", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${colors.bg} ${colors.text}`}
                    >
                      {EVENT_TYPE_LABELS[event.type]}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingHitList;
